<script setup lang="ts">
import { reactive } from 'vue';
import { hash } from 'ohash';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';

const BROKER_PASS_URL = '/broker-pass/';
const BROKER_PASS_KEY = 'BROKER_PASS_KEY';

export interface BrokerPassForm {
  first_name: string;
  last_name: string;
  person_type: 'individual' | 'company';
  company: string;
  email: string;
  phone: string;
  email_consent: boolean;
  position: string;
  field_of_activity: string;
}

const resetFormData: BrokerPassForm = {
  first_name: '',
  last_name: '',
  person_type: 'individual',
  company: '',
  email: '',
  email_consent: false,
  phone: '',
  position: '',
  field_of_activity: '',
};

const formData = reactive({ ...resetFormData });

const submitForm = async () => {
  const requiredFields = [
    { value: formData.first_name, name: 'Имя' },
    { value: formData.last_name, name: 'Фамилия' },
    { value: formData.email, name: 'E-mail' },
  ];

  const emptyFields = requiredFields
    .filter((field) => !field.value.trim())
    .map((field) => field.name);

  if (emptyFields.length > 0) {
    alert(`Пожалуйста, заполните обязательные поля: ${emptyFields.join(', ')}`);
    return;
  }

  const formHash = hash(formData);

  const options = {
    method: 'POST' as const,
    body: { ...formData },
    key: `${BROKER_PASS_KEY}_${formHash}`,
  };

  await useCustomFetch(BROKER_PASS_URL, options);

  Object.assign(formData, resetFormData);

  window.scrollTo({
    top: 0,
    behavior: 'instant',
  });

  alert('Ваши данные отправлены');
};
</script>
<template>
  <section id="get-broker-pass" class="registration-section">
    <h2 class="registration-title">Получить свой Брокер Пасс</h2>
    <div class="registration-intro">
      <div class="intro-headline">
        Заполни короткую форму и стань частью сообщества
      </div>
      <div class="intro-description">
        После регистрации получишь персональное письмо с подтверждением и
        ссылкой на регистрацию на бесплатное мероприятие.
      </div>
    </div>
    <div class="registration-form-container">
      <div class="form-fields">
        <div class="form-field">
          <label class="form-label">
            <span class="label-text">Имя:</span>
            <span class="label-required">*&nbsp;</span>
          </label>
          <input
            v-model="formData.first_name"
            type="text"
            class="form-input"
            placeholder=""
          />
        </div>
        <div class="form-field">
          <label class="form-label">
            <span class="label-text">Фамилия:</span>
            <span class="label-required">*&nbsp;</span>
          </label>
          <input
            v-model="formData.last_name"
            type="text"
            class="form-input"
            placeholder=""
          />
        </div>
        <div class="status-selection-field">
          <span class="status-label">Статус:</span>
          <div
            class="status-option"
            :class="{
              'status-option--active': formData.person_type === 'individual',
            }"
            @click="formData.person_type = 'individual'"
          >
            <span class="status-text">Частное лицо</span>
          </div>
          <div
            class="status-option"
            :class="{
              'status-option--active': formData.person_type === 'company',
            }"
            @click="formData.person_type = 'company'"
          >
            <span class="status-text">Компания</span>
          </div>
        </div>
        <div class="form-field">
          <label class="form-label">
            <span class="label-text">Компания:&nbsp;</span>
          </label>
          <input
            v-model="formData.company"
            type="text"
            class="form-input"
            placeholder=""
          />
        </div>
        <div class="form-field">
          <label class="form-label">
            <span class="label-text">Должность:&nbsp;</span>
          </label>
          <input
            v-model="formData.position"
            type="text"
            class="form-input"
            placeholder=""
          />
        </div>
        <div class="form-field">
          <label class="form-label">
            <span class="label-text">Сфера деятельности:&nbsp;</span>
          </label>
          <input
            v-model="formData.field_of_activity"
            type="text"
            class="form-input"
            placeholder=""
          />
        </div>
        <div class="form-field">
          <label class="form-label">
            <span class="label-text">Электронная почта:</span>
            <span class="label-required">*&nbsp;</span>
          </label>
          <input
            v-model="formData.email"
            type="text"
            class="form-input"
            placeholder=""
          />
        </div>
        <div class="form-field">
          <input
            v-model="formData.phone"
            type="tel"
            class="form-input"
            placeholder="+7 (___) ___-__-__"
          />
        </div>
      </div>

      <div class="form-footer">
        <div
          class="form-checkbox"
          @click="formData.email_consent = !formData.email_consent"
        >
          <div class="checkbox-wrapper">
            <div class="checkbox-icon-container">
              <img
                v-if="formData.email_consent"
                class="checkbox-glyph"
                src="./images/checkbox.svg"
                alt="Checkbox"
              />
            </div>
            <div class="checkbox-label-container">
              <div class="checkbox-label">
                Даю <span class="checkbox-link">согласие на рассылку</span> по
                e-mail
              </div>
            </div>
          </div>
        </div>
        <div class="form-legal-note">
          Продолжая, вы принимаете
          <a
            href="https://cre.ru/politika-konfidentsialnosti"
            target="_blank"
            rel="noopener noreferrer"
            class="legal-note-link"
          >
            Политику конфиденциальности
          </a>
          и&nbsp;даете
          <a
            href="https://cre.ru/privacy_policy"
            target="_blank"
            rel="noopener noreferrer"
            class="legal-note-link"
          >
            Согласие на&nbsp;обработку персональных данных
          </a>
        </div>
      </div>

      <div class="form-submit-button" @click="submitForm">
        <div class="submit-button-text">Получить Брокер Пасс</div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use 'sass:color';
@use './styles/styles.scss' as *;
.registration-section {
  background: #ffffff url('./images/form-back.png') no-repeat left
    calc(50% + 370px) top;
  max-width: 1440px;
  padding: 80px 0 65px 129px;
  position: relative;

  @media (max-width: $tablet-view) {
    padding: 90px 0 90px 30px;
    background-position: left calc(50% + 85px + 370px) top;
    height: unset;
  }

  @media (max-width: $mobile-view) {
    width: 100%;
    box-sizing: border-box;
    background-image: none;
    padding: 40px 20px;
  }

  .registration-title {
    color: #313030;
    @include font-regular;
    font-size: 64px;
    line-height: 100%;
    margin: 0 0 0 -7px;
    width: 400px;

    @media (max-width: $tablet-view) {
      font-size: 46px;
      width: calc(50% + 55px);
      margin-left: -3px;
    }

    @media (max-width: $mobile-view) {
      font-size: 32px;
      width: 270px;
      margin-left: 0;
    }
  }

  .registration-intro {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 470px;
    max-width: calc(50% - 70px);
    margin-top: 20px;

    @media (max-width: $tablet-view) {
      margin-top: 30px;
      width: calc(50% + 55px);
      max-width: none;
    }

    @media (max-width: $mobile-view) {
      width: 100%;
      box-sizing: border-box;
      font-size: 16px;
      margin-top: 20px;
    }

    .intro-headline {
      color: #313030;
      @include font-regular;
      font-size: 32px;
      line-height: 130%;

      @media (max-width: $tablet-view) {
        font-size: 24px;
      }
    }

    .intro-description {
      color: #313030;
      @include font-regular;
      font-size: 16px;
      line-height: 120%;

      @media (max-width: $tablet-view) {
        font-size: 14px;
      }
    }
  }

  .registration-form-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 618px;
    max-width: calc(50% - 70px);
    position: relative;
    margin-top: 41px;

    @media (max-width: $tablet-view) {
      margin-top: 30px;
      width: 433px;
      max-width: calc(50% + 75px);
    }

    @media (max-width: $mobile-view) {
      margin-top: 40px;
      width: 100%;
      box-sizing: border-box;
      max-width: unset;
    }

    .form-fields {
      display: flex;
      flex-direction: column;
      gap: 10px;
      width: 100%;
      max-width: calc(100% - 70px);

      @media (max-width: $tablet-view) {
        max-width: 100%;
        gap: 6px;
      }

      @media (max-width: $mobile-view) {
        max-width: unset;
        gap: 16px;
      }

      .form-field {
        display: flex;
        flex-direction: row;
        align-items: baseline;
        padding-bottom: 6px;
        border-bottom: 1px solid #999999;

        .form-label {
          display: flex;
          flex-direction: row;
          gap: 6px;
          @include font-regular;
          font-size: 16px;
          line-height: 120%;
          white-space: nowrap;

          .label-text {
            color: #313030;
          }

          .label-required {
            color: #d43747;
          }
        }

        .form-input {
          flex: 1;
          @include font-regular;
          font-size: 16px;
          line-height: 120%;
          color: #313030;
          border: none;
          background: transparent;
          outline: none;
          padding: 0;

          &::placeholder {
            color: #999999;
          }
        }
      }

      .status-selection-field {
        display: flex;
        flex-direction: row;
        gap: 10px;
        align-items: center;

        .status-label {
          color: var(--dark-charcoal, #313030);
          @include font-regular;
          font-size: 16px;
          line-height: 120%;
        }

        .status-option {
          border-radius: 12px;
          border: 1px solid var(--ice, #8497e0);
          padding: 5px 10px;
          display: flex;
          flex-direction: row;
          gap: 10px;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          user-select: none;

          .status-text {
            color: #313030;
            @include font-regular;
            font-size: 16px;
            line-height: 120%;
          }

          &--active {
            border-color: #f26628;
          }
        }
      }
    }

    .form-footer {
      display: flex;
      flex-direction: column;
      gap: 8px;
      max-width: calc(100% - 70px);

      @media (max-width: $tablet-view) {
        max-width: 100%;
      }

      @media (max-width: $mobile-view) {
        gap: 10px;
      }

      .form-checkbox {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .checkbox-wrapper {
          display: flex;
          flex-direction: row;
          gap: 8px;
          align-items: center;
          width: 290px;
          cursor: pointer;

          .checkbox-icon-container {
            flex-shrink: 0;
            width: 12px;
            height: 12px;
            position: relative;
            border: 1px solid #999999;
            border-radius: 2px;
            box-sizing: border-box;
            display: flex;
            align-items: center;
            justify-content: center;

            .checkbox-glyph {
              width: 12px;
              height: 12px;
              position: relative;
              left: 0;
              top: 0;
            }
          }

          .checkbox-label-container {
            display: flex;
            flex-direction: row;
            gap: 12px;
            align-items: center;
            flex-shrink: 0;
            height: 22px;

            .checkbox-label {
              color: #999999;
              @include font-regular;
              font-size: 12px;
              line-height: 120%;

              .checkbox-link {
                text-decoration: underline;
              }
            }
          }
        }
      }

      .form-legal-note {
        color: #999999;
        @include font-regular;
        font-size: 12px;
        line-height: 120%;

        .legal-note-link {
          color: inherit;
          text-decoration: underline;
        }
      }
    }

    .form-submit-button {
      display: flex;
      height: 54px;
      padding: 15px 40px;
      justify-content: center;
      align-items: center;
      border-radius: 6px;
      border: 0 solid #111;
      background: #f26628;
      width: fit-content;
      cursor: pointer;
      user-select: none;

      &:hover {
        background: color.adjust(#f26628, $lightness: -5%);
      }

      @media (max-width: $mobile-view) {
        box-sizing: border-box;
        width: 100%;
      }

      .submit-button-text {
        @include font-medium;
        color: #fff;
        font-size: 24px;
        line-height: 100%; /* 24px */

        @media (max-width: $mobile-view) {
          font-size: 16px;
        }
      }
    }
  }
}
</style>
